// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';
import React, { useCallback } from 'react';
import Modal from 'antd/lib/modal';
import { LoadingOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { DimensionType, CVATCore } from 'cvat-core-wrapper';
import Menu, { MenuInfo } from 'components/dropdown-menu';
import { usePlugins } from 'utils/hooks';
import { CombinedState } from 'reducers';

import Upload from 'antd/lib/upload';
import Button from 'antd/lib/button';
import { t } from 'utils/i18n';

type AnnotationFormats = Awaited<ReturnType<CVATCore['server']['formats']>>;

interface Props {
    taskID: number;
    projectID: number | null;
    taskMode: string;
    bugTracker: string;
    loaders: AnnotationFormats['loaders'];
    dumpers: AnnotationFormats['dumpers'];
    inferenceIsActive: boolean;
    taskDimension: DimensionType;
    backupIsActive: boolean;
    onClickMenu: (params: MenuInfo) => void;
}

export enum Actions {
    LOAD_TASK_ANNO = 'load_task_anno',
    LOAD_TASK_REFERENCE = 'load_task_reference',
    EXPORT_TASK_DATASET = 'export_task_dataset',
    DELETE_TASK = 'delete_task',
    RUN_AUTO_ANNOTATION = 'run_auto_annotation',
    MOVE_TASK_TO_PROJECT = 'move_task_to_project',
    OPEN_BUG_TRACKER = 'open_bug_tracker',
    BACKUP_TASK = 'backup_task',
    VIEW_ANALYTICS = 'view_analytics',
    MODEL_TASK = 'model_task',
}

function ActionsMenuComponent(props: Props): JSX.Element {
    const {
        taskID,
        projectID,
        bugTracker,
        inferenceIsActive,
        backupIsActive,
        onClickMenu,
    } = props;

    const plugins = usePlugins((state: CombinedState) => state.plugins.components.taskActions.items, props);

    const onClickMenuWrapper = useCallback(
        (params: MenuInfo) => {
            if (!params) {
                return;
            }

            if (params.key === Actions.DELETE_TASK) {
                Modal.confirm({
                    title: t('The task {id} will be deleted', { id: taskID }),
                    content: t('All related data (images, annotations) will be lost. Continue?'),
                    className: 'cvat-modal-confirm-delete-task',
                    onOk: () => {
                        onClickMenu(params);
                    },
                    okButtonProps: {
                        type: 'primary',
                        danger: true,
                    },
                    okText: t('Delete'),
                });
            } else if (params.key === Actions.MODEL_TASK) {
                Modal.confirm({
                    title: t('Automatic models search for the task {id}', { id: taskID }),
                    // content: t('All related data (images, annotations) will be lost. Continue?'),
                    className: 'cvat-modal-confirm-delete-reference',
                    onOk: () => {
                        onClickMenu(params);
                    },
                    okButtonProps: {
                        type: 'primary',
                        // danger: true,
                    },
                    okText: t('Find'),
                });
            }else if (params.key === Actions.LOAD_TASK_REFERENCE) {

                const baseURL = window.location.origin;
                let fileList: any[] = [];

                Modal.confirm({
                    title: t('Add images'),
                    icon: null,
                    content: (
                        <div>
                            <Upload
                                multiple
                                showUploadList={{
                                    showPreviewIcon: false,
                                    showRemoveIcon: true,
                                }}
                                onChange={(info) => {
                                    const { file, fileList: newFileList } = info;
                                    fileList = newFileList;
                                }}
                                itemRender={(originNode, file, currFileList, actions) => {
                                    return (
                                        <div>
                                            {file.name}
                                            <DeleteOutlined
                                                onClick={actions.remove}
                                                style={{ marginLeft: 8, cursor: 'pointer' }}
                                            />
                                        </div>
                                    );
                                }}

                            >
                                <Button icon={<UploadOutlined />}>{t('Select Files')}</Button>
                            </Upload>
                        </div>
                    ),
                    className: 'cvat-modal-confirm-remove-annotation',
                    onOk: () => {
                        // Create a new FormData object
                        const formData = new FormData();

                        // Append files to the FormData object
                        fileList.forEach((file, index) => {
                            if (file.originFileObj instanceof File) {
                                formData.append(`client_files[${index}]`, file.originFileObj);
                            } else {
                                console.error('File is not an instance of File:', file);
                            }
                        });

                        // Get the CSRF token from the meta tag
                        function getCookie(name: string) {
                            const value = `; ${document.cookie}`;
                            const parts = value.split(`; ${name}=`);
                            if (parts.length === 2) return parts.pop().split(';').shift();
                        }

                        const csrfToken = getCookie('csrftoken');

                        // Send the FormData to the server
                        fetch(`${baseURL}/api/tasks/${taskID}/data`, {
                            method: 'PATCH',
                            headers: {
                                'X-CSRFToken': csrfToken,
                            },
                            body: formData, // Use FormData instead of JSON
                        })
                        .then(response => response.json())
                        .then(data => {
                            console.log('Success:', data);
                            // window.location.reload();
                        })
                        .catch((error) => {
                            console.error('Error:', error);
                        });
                    },
                    okButtonProps: {
                        type: 'primary',
                    },
                    okText: t('Add'),
                });
            }  else {
                onClickMenu(params);
            }
        },
        [taskID],
    );

    const menuItems: [JSX.Element, number][] = [];
    menuItems.push([(
        <Menu.Item key={Actions.LOAD_TASK_ANNO}>{t('Upload annotations')}</Menu.Item>
    ), 0]);

    menuItems.push([(
        <Menu.Item key={Actions.LOAD_TASK_REFERENCE}>{t('Add images')}</Menu.Item>
    ), 0]);

    menuItems.push([(
        <Menu.Item key={Actions.EXPORT_TASK_DATASET}>{t('Export task dataset')}</Menu.Item>
    ), 10]);

    if (bugTracker) {
        menuItems.push([(
            <Menu.Item key={Actions.OPEN_BUG_TRACKER}>{t('Open bug tracker')}</Menu.Item>
        ), 20]);
    }

    menuItems.push([(
        <Menu.Item disabled={inferenceIsActive} key={Actions.RUN_AUTO_ANNOTATION}>
            {t('Automatic annotation')}
        </Menu.Item>
    ), 30]);

    menuItems.push([(
        <React.Fragment key={Actions.MODEL_TASK}>
            <Menu.Item key={Actions.MODEL_TASK} disabled={inferenceIsActive}>
                {t('Automatic 3d model search')}
            </Menu.Item>
        </React.Fragment>
    ), 30]);

    menuItems.push([(
        <Menu.Item
            key={Actions.BACKUP_TASK}
            disabled={backupIsActive}
            icon={backupIsActive && <LoadingOutlined id='cvat-backup-task-loading' />}
        >
            {t('Backup Task')}
        </Menu.Item>
    ), 40]);

    menuItems.push([(
        <Menu.Item
            key={Actions.VIEW_ANALYTICS}
        >
            {t('View analytics')}
        </Menu.Item>
    ), 50]);

    if (projectID === null) {
        menuItems.push([(
            <Menu.Item key={Actions.MOVE_TASK_TO_PROJECT}>{t('Move to project')}</Menu.Item>
        ), 60]);
    }

    menuItems.push([(
        <React.Fragment key={Actions.DELETE_TASK}>
            <Menu.Divider />
            <Menu.Item key={Actions.DELETE_TASK}>{t('Delete')}</Menu.Item>
        </React.Fragment>
    ), 70]);

    menuItems.push(
        ...plugins.map(({ component: Component, weight }, index) => {
            const menuItem = Component({ key: index, targetProps: props });
            return [menuItem, weight] as [JSX.Element, number];
        }),
    );

    return (
        <Menu
            selectable={false}
            className='cvat-actions-menu'
            onClick={onClickMenuWrapper}
        >
            { menuItems.sort((menuItem1, menuItem2) => menuItem1[1] - menuItem2[1])
                .map((menuItem) => menuItem[0]) }
        </Menu>
    );
}

export default React.memo(ActionsMenuComponent);
